/**
 * Files section — review & manage a submission's documents.
 *
 * Two tabs (per Andressa's Figma):
 *   • Generated Files — formal system outputs, one download row each, in
 *     the users' requested order: Pricing Model (OR-1351, .xlsx export),
 *     Quote, Binder, Policy, TRIA.
 *   • Uploaded Files — supporting docs the UW attaches. Dense table with
 *     inline download + delete; "Upload Documents" button. Backed by the
 *     real `/v2/submissions/{id}/files/` API.
 *
 * A Dropbox banner shows only when the submission carries a
 * `dropbox_url` (external documents live there).
 *
 * `uploaded_by` comes back as a numeric user id; name/avatar resolution
 * is pending a backend users endpoint, so we render the id best-effort.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { Key } from "react-aria-components";
import { Radio as AriaRadio, RadioGroup as AriaRadioGroup } from "react-aria-components";
import {
    AlertCircle,
    ArrowDown,
    ArrowUp,
    ChevronSelectorVertical,
    Download01,
    Folder,
    Trash02,
    UploadCloud02,
    XClose,
} from "@untitledui/icons";
import { Avatar, Button, ButtonUtility, Table, Tabs, cx } from "@tricura/atlas-ui";
import {
    ApiError,
    deleteSubmissionFile,
    exportPricingModelWorkbook,
    fromWireSubmission,
    pricingModelIssues,
    generateBinderDocument,
    generatePolicyDocument,
    generateQuoteDocument,
    generateTriaDocument,
    getPolicyForSubmission,
    getQuoteUpdatedAt,
    listSubmissionFiles,
    uploadSubmissionFile,
} from "../../../api";
import type { SubmissionFile } from "../../../api";
import infoIcon from "../../../assets/files/external-docs-info.svg";
import extLinkIcon from "../../../assets/files/external-link.svg";
import fileDocxIcon from "../../../assets/files/file-docx.svg";
import filePdfIcon from "../../../assets/files/file-pdf.svg";
import fileXlsxIcon from "../../../assets/files/file-xlsx.svg";
import { TableEmptyState } from "../../../components/table-empty-state";
import { UploadMenu, type UploadMenuAction } from "../../../components/upload-menu";
import { WarningDialog } from "../../../components/warning-dialog";
import { isFlagOn } from "../../../config/flags";
import {
    getSubmissionFilesRevision,
    getUploadStatusSnapshot,
    isUploadBusy,
    startAttachBatch,
    startPricingImport,
    subscribeUploadStatus,
} from "../../../state/upload-status";
import { formatDateTime } from "../../../utils/format-datetime";
import { useSubmissionContext } from "../context";

/** Andressa's exported arrow-circle-up-right glyph for the banner button. */
function ExternalLinkIcon() {
    return <img src={extLinkIcon} alt="" aria-hidden="true" className="size-5 shrink-0" />;
}

/** The most recent of two ISO datetimes (empty/invalid values lose). */
function latestIso(a: string, b: string): string {
    const ta = Date.parse(a);
    const tb = Date.parse(b);
    if (Number.isNaN(ta)) return Number.isNaN(tb) ? "" : b;
    if (Number.isNaN(tb)) return a;
    return tb > ta ? b : a;
}

/** File-type icon (Andressa's exported SVGs) shown left of a generated doc. */
const FILE_TYPE_ICONS: Record<string, string> = {
    DOCX: fileDocxIcon,
    XLSX: fileXlsxIcon,
    PDF: filePdfIcon,
};

function FileBadge({ ext }: { ext: string }) {
    const icon = FILE_TYPE_ICONS[ext] ?? filePdfIcon;
    return <img src={icon} alt={ext} className="size-9 shrink-0" />;
}

/** A single generated-document row (Quote / Binder) — Andressa's Figma. */
function DocRow({
    name,
    ext,
    dateLabel,
    loading,
    disabled,
    "data-autotest": dataAutotest,
    onDownload,
}: {
    name: string;
    ext: string;
    dateLabel: string;
    loading: boolean;
    "data-autotest"?: string;
    disabled?: boolean;
    onDownload: () => void;
}) {
    return (
        <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6" data-autotest={dataAutotest}>
            <div className="flex items-center gap-3">
                <FileBadge ext={ext} />
                <div className="flex flex-col">
                    <span className="text-primary text-sm font-medium">{name}</span>
                    {dateLabel && <span className="text-tertiary text-xs">Updated {dateLabel}</span>}
                </div>
            </div>
            <Button
                color="secondary"
                size="sm"
                iconLeading={Download01}
                onClick={onDownload}
                isLoading={loading}
                isDisabled={disabled}
            >
                Download
            </Button>
        </div>
    );
}

type FilesTab = "generated" | "uploaded";

/** Stable empty reference so the sort memo doesn't churn while loading. */
const EMPTY_FILES: SubmissionFile[] = [];

const TAB_ITEMS = [
    { id: "generated", label: "Generated Files", "data-autotest": "files-generated-tab" },
    { id: "uploaded", label: "Uploaded Files", "data-autotest": "files-uploaded-tab" },
];

type ListState = { status: "loading" } | { status: "ready"; files: SubmissionFile[] } | { status: "error"; message: string };

export function FilesPage() {
    const { detail, setDetail } = useSubmissionContext();
    const [tab, setTab] = useState<FilesTab>("generated");
    const [state, setState] = useState<ListState>({ status: "loading" });
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    /** OR-1400 — From Broker Documents. Separate picker (extraction
     *  formats only) + the "this will update your data" confirmation that
     *  gates it. The batch itself lives in the global upload-status store
     *  so it survives navigation; while it runs, every upload entry point
     *  is disabled (one upload at a time across the product). */
    const brokerInputRef = useRef<HTMLInputElement>(null);
    const [confirmBrokerUpload, setConfirmBrokerUpload] = useState(false);
    const uploadStatus = useSyncExternalStore(subscribeUploadStatus, getUploadStatusSnapshot, getUploadStatusSnapshot);
    const uploadBusy = isUploadBusy(uploadStatus);
    /** Figma popup 2 — a picked file's name already exists on the
     *  submission. Holds the batch until the user picks Keep both /
     *  Replace existing (replace = delete the old record(s) first). */
    const [duplicatePrompt, setDuplicatePrompt] = useState<null | {
        kind: "attach-only" | "broker";
        files: File[];
        duplicateNames: string[];
    }>(null);
    const [duplicateChoice, setDuplicateChoice] = useState<"keep" | "replace">("keep");
    /** OR-1351 — From Pricing Model. Its own picker (.xlsx only) behind a
     *  destructive confirmation: unlike broker documents, which only fill
     *  the fields they carry, a workbook import REPLACES whole sections.
     *  The import itself runs through the floating panel, like every
     *  other upload. */
    const pricingInputRef = useRef<HTMLInputElement>(null);
    const [confirmPricingImport, setConfirmPricingImport] = useState(false);
    /** Every bad cell the backend found, listed together so the user fixes
     *  the workbook in a single pass. Arrives on the failed job's `error`
     *  now; a rejected upload still comes back as a 422. */
    const [pricingErrors, setPricingErrors] = useState<Array<{ sheet?: string; cell?: string; message: string }>>([]);
    // Generated Files (Pricing Model / Quote / Binder / Policy / TRIA).
    const [generating, setGenerating] = useState<null | "pricing" | "quote" | "binder" | "policy" | "tria">(null);
    /** OR-1424 — TRIA row. Off by default until it's ready; see
     *  `config/flags`. The Policy row shipped ungated. */
    const showTria = isFlagOn("tria_document");
    /** Policy id for the Binder (a policy must exist first). */
    const [policyId, setPolicyId] = useState<string | null>(null);
    /** "Updated" stamps for the generated docs (OR-1324). The DOCX files are
     *  produced on demand — there is no stored file record — so the stamp is
     *  the `updated_at` of the resource each document is generated from:
     *  Quote ← the quote, Binder ← the policy (or the quote if newer, since
     *  the binder's content also reflects the premium walk). Fetched on every
     *  mount so a Premium Selection save shows up as soon as the user comes
     *  back to Files. Empty → fall back to the submission's `updatedAt`.
     *
     *  OR-1424: Policy and TRIA share the Binder's stamp. Read from
     *  orion-backend PR #326 — `render_policy_docx`'s docstring says
     *  "OR-1425 maps Submission/Policy ORM records onto this", and `DecFill`
     *  bears that out: `policy_number` off the policy, the Coverage A/B/C
     *  limits, `premium`, `fees` and `total` off the quote. `TriaFill` is the
     *  same pair (policy number + the quote's TRIA premium). So all three
     *  documents move together with the policy and the premium walk. */
    const [quoteStamp, setQuoteStamp] = useState("");
    const [policyDocStamp, setPolicyDocStamp] = useState("");

    const dropboxUrl = detail.dropboxUrl;

    // The Binder is generated from a POLICY, not the submission — resolve
    // the submission's policy id so we can enable/disable the CTA. The same
    // fetch (plus the quote GET) carries the docs' `updated_at` stamps.
    useEffect(() => {
        if (!detail.id) {
            setPolicyId(null);
            setQuoteStamp("");
            setPolicyDocStamp("");
            return;
        }
        let cancelled = false;
        void Promise.all([getPolicyForSubmission(detail.id).catch(() => null), getQuoteUpdatedAt(detail.id)]).then(
            ([policy, quoteUpdatedAt]) => {
                if (cancelled) return;
                setPolicyId(policy?.id ?? null);
                setQuoteStamp(quoteUpdatedAt);
                setPolicyDocStamp(latestIso(policy?.updated_at ?? "", quoteUpdatedAt));
            },
        );
        return () => {
            cancelled = true;
        };
    }, [detail.id]);

    // Trigger a browser download for a generated blob under `filename`.
    const saveBlob = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.rel = "noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    };

    // Download a generated document blob — the backend returns a Word
    // doc (.docx) today, so derive the extension from the MIME type
    // (falls back to .pdf if it ever switches).
    const openDocument = (blob: Blob, baseName: string) => {
        const ext = blob.type.includes("wordprocessing") ? "docx" : blob.type.includes("pdf") ? "pdf" : "docx";
        saveBlob(blob, `${baseName}.${ext}`);
    };

    // OR-1351 — the pricing-model workbook export. The backend names the
    // file (`Pricing Model - {FNI} - {date}.xlsx`); when the header isn't
    // exposed to the browser we rebuild the same shape locally.
    const onExportPricingModel = useCallback(async () => {
        if (!detail.id) return;
        setGenerating("pricing");
        setUploadError(null);
        try {
            const { blob, filename } = await exportPricingModelWorkbook(detail.id);
            const today = new Date().toISOString().slice(0, 10);
            const insured = detail.accountBasics.fni || detail.accountBasics.title || detail.id;
            saveBlob(blob, filename ?? `Pricing Model - ${insured} - ${today}.xlsx`);
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : "Couldn't export the Pricing Model workbook.");
        } finally {
            setGenerating(null);
        }
    }, [detail.id, detail.accountBasics.fni, detail.accountBasics.title]);

    const onGenerateQuote = useCallback(async () => {
        if (!detail.id) return;
        setGenerating("quote");
        setUploadError(null);
        try {
            openDocument(await generateQuoteDocument(detail.id), `quote-${detail.id}`);
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : "Couldn't generate the Quote document.");
        } finally {
            setGenerating(null);
        }
    }, [detail.id]);

    const onGenerateBinder = useCallback(async () => {
        if (!policyId) {
            setUploadError("Add a Policy Number in Policy Details before generating the Binder.");
            return;
        }
        setGenerating("binder");
        setUploadError(null);
        try {
            openDocument(await generateBinderDocument(policyId), `binder-${policyId}`);
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : "Couldn't generate the Binder document.");
        } finally {
            setGenerating(null);
        }
    }, [policyId]);

    // OR-1424. Policy is policy-scoped, same gate as the Binder — a
    // Policy Number must exist first. TRIA stays submission-scoped and
    // ungated by design (bought mid-term).
    const onGeneratePolicy = useCallback(async () => {
        if (!policyId) {
            setUploadError("Add a Policy Number in Policy Details before generating the Policy.");
            return;
        }
        setGenerating("policy");
        setUploadError(null);
        try {
            openDocument(await generatePolicyDocument(policyId), `policy-${policyId}`);
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : "Couldn't generate the Policy document.");
        } finally {
            setGenerating(null);
        }
    }, [policyId]);

    const onGenerateTria = useCallback(async () => {
        if (!detail.id) return;
        setGenerating("tria");
        setUploadError(null);
        try {
            openDocument(await generateTriaDocument(detail.id), `tria-${detail.id}`);
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : "Couldn't generate the TRIA document.");
        } finally {
            setGenerating(null);
        }
    }, [detail.id]);

    const load = useCallback(async () => {
        if (!detail.id) {
            setState({ status: "ready", files: [] });
            return;
        }
        setState({ status: "loading" });
        try {
            const files = await listSubmissionFiles(detail.id);
            setState({ status: "ready", files });
        } catch (error) {
            setState({ status: "error", message: error instanceof Error ? error.message : "Failed to load files" });
        }
    }, [detail.id]);

    useEffect(() => {
        void load();
    }, [load]);

    const onPickFiles = () => fileInputRef.current?.click();

    // The Upload ▾ menu's three paths (Import/Export Flow V2). Pricing
    // Model is OR-1351's import — wired when that flow lands; the menu
    // item is present so the shell matches the approved design.
    const onUploadMenuAction = useCallback((action: UploadMenuAction) => {
        if (action === "attach-only") {
            fileInputRef.current?.click();
        } else if (action === "broker-documents") {
            setConfirmBrokerUpload(true);
        } else if (action === "pricing-model") {
            setConfirmPricingImport(true);
        }
    }, []);

    const onPricingFileSelected = useCallback(
        (fileList: FileList | null) => {
            const file = fileList?.[0];
            if (pricingInputRef.current) pricingInputRef.current.value = "";
            if (!file || !detail.id) return;
            setUploadError(null);
            setPricingErrors([]);
            // The floating panel owns it from here (Import/Export Flow V2):
            // one row for the workbook with its progress, Failed + Try again,
            // and Update Submission when the job completes — which is what
            // refetches the detail and raises the Auto-fill toast, exactly
            // like a broker-documents batch. The import overwrites the
            // submission's imported sections in one transaction, so a failed
            // job changed nothing and the workbook can be fixed and retried.
            // `false` means the single upload slot is taken — near impossible
            // here (every entry point disables on busy), but a silent no-op
            // would read as a broken button if a race ever lets it through.
            if (!startPricingImport(file, detail.id)) {
                setUploadError("Another upload is already in progress. Wait for it to finish and try again.");
            }
        },
        [detail.id],
    );


    const uploadAttachOnly = useCallback(
        async (picked: File[]) => {
            if (!detail.id) return;
            setUploading(true);
            setUploadError(null);
            try {
                // Upload sequentially so a mid-batch failure still surfaces
                // the ones that succeeded on the reload.
                for (const file of picked) {
                    await uploadSubmissionFile(detail.id, file);
                }
                await load();
            } catch (error) {
                setUploadError(error instanceof Error ? error.message : "An unexpected error occurred");
            } finally {
                setUploading(false);
            }
        },
        [detail.id, load],
    );

    /** Run a picked batch down its path, optionally deleting the existing
     *  same-named records first (the duplicate popup's "Replace existing"). */
    const runUpload = useCallback(
        async (kind: "attach-only" | "broker", picked: File[], replaceExisting: boolean) => {
            if (!detail.id) return;
            if (replaceExisting && state.status === "ready") {
                const names = new Set(picked.map((f) => f.name));
                try {
                    for (const existing of state.files.filter((f) => names.has(f.name))) {
                        await deleteSubmissionFile(detail.id, existing.id);
                    }
                } catch (error) {
                    setUploadError(error instanceof Error ? error.message : "Couldn't replace the existing file.");
                    await load();
                    return;
                }
            }
            if (kind === "attach-only") {
                await uploadAttachOnly(picked);
                return;
            }
            // The store owns the product's single upload slot. The duplicate
            // prompt defers this dispatch, so another surface can claim the
            // slot in between; say so rather than dropping the files.
            if (!startAttachBatch(detail.id, picked)) {
                setUploadError("Another upload is already running. Wait for it to finish and try again.");
            }
        },
        [detail.id, state, load, uploadAttachOnly],
    );

    /** Entry point for both pickers: intercept same-named files with the
     *  Duplicate File popup before anything is sent. */
    const beginUpload = useCallback(
        (kind: "attach-only" | "broker", fileList: FileList | null) => {
            if (!fileList || fileList.length === 0 || !detail.id) return;
            const picked = Array.from(fileList);
            if (fileInputRef.current) fileInputRef.current.value = "";
            if (brokerInputRef.current) brokerInputRef.current.value = "";
            const existingNames = new Set(state.status === "ready" ? state.files.map((f) => f.name) : []);
            const duplicateNames = [...new Set(picked.map((f) => f.name).filter((n) => existingNames.has(n)))];
            if (duplicateNames.length > 0) {
                setDuplicateChoice("keep");
                setDuplicatePrompt({ kind, files: picked, duplicateNames });
                return;
            }
            void runUpload(kind, picked, false);
        },
        [detail.id, state, runUpload],
    );

    // Reload the table whenever the upload store touches the submission's
    // stored files (a batch settling, or a row being removed). Skips its
    // own first value — the mount effect above already loaded once.
    const filesRevision = useSyncExternalStore(subscribeUploadStatus, getSubmissionFilesRevision, getSubmissionFilesRevision);
    const seenRevision = useRef(filesRevision);
    useEffect(() => {
        if (filesRevision === seenRevision.current) return;
        seenRevision.current = filesRevision;
        void load();
    }, [filesRevision, load]);

    const onDelete = useCallback(
        async (fileId: string) => {
            if (!detail.id) return;
            try {
                await deleteSubmissionFile(detail.id, fileId);
                await load();
            } catch (error) {
                setUploadError(error instanceof Error ? error.message : "An unexpected error occurred");
            }
        },
        [detail.id, load],
    );

    const files = useMemo(() => (state.status === "ready" ? state.files : EMPTY_FILES), [state]);

    // Client-side sort on File Name (cycle: none → asc → desc → none).
    const [sortDir, setSortDir] = useState<"asc" | "desc" | null>(null);
    const cycleSort = () => setSortDir((d) => (d === null ? "asc" : d === "asc" ? "desc" : null));
    const sortedFiles = useMemo(() => {
        if (!sortDir) return files;
        const arr = [...files].sort((a, b) => a.name.localeCompare(b.name));
        return sortDir === "desc" ? arr.reverse() : arr;
    }, [files, sortDir]);

    return (
        <div className="w-full space-y-6">
            <header className="flex flex-col gap-1">
                <h2 className="text-primary text-md font-semibold">Files</h2>
                <p className="text-tertiary text-sm">Review and manage documents for this submission</p>
            </header>

            {/* Dropbox / external-documents banner — only when the
             * submission carries an external link. */}
            {dropboxUrl && (
                <div className="border-secondary bg-primary shadow-xs flex items-center justify-between gap-3 rounded-xl border px-4 py-3">
                    <span className="inline-flex items-center gap-3">
                        <img src={infoIcon} alt="" aria-hidden="true" className="size-10 shrink-0" />
                        <span className="text-tertiary text-sm">Some documents for this submission are stored externally</span>
                    </span>
                    <Button color="secondary" size="sm" iconTrailing={ExternalLinkIcon} href={dropboxUrl} target="_blank" rel="noreferrer">
                        View External Documents
                    </Button>
                </div>
            )}

            {/* Upload error toast (dismissible). */}
            {uploadError && (
                <div
                    role="alert"
                    className="border-error-secondary bg-error-primary text-error-primary flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm"
                >
                    <span className="inline-flex items-center gap-2">
                        <AlertCircle aria-hidden="true" className="size-4" />
                        {uploadError}
                    </span>
                    <button type="button" aria-label="Dismiss" onClick={() => setUploadError(null)}>
                        <XClose aria-hidden="true" className="size-4" />
                    </button>
                </div>
            )}

            {/* A rejected workbook comes back as ONE 422 listing every bad
              * cell, so they are all shown at once: the underwriter fixes
              * the spreadsheet in a single pass instead of re-uploading to
              * discover the next problem. Nothing was written — the import
              * is transactional. */}
            {pricingErrors.length > 0 && (
                <div
                    role="alert"
                    className="border-error-subtle bg-error-primary flex flex-col gap-2 rounded-xl border p-4"
                >
                    <div className="flex items-start justify-between gap-3">
                        <span className="text-error-primary inline-flex items-center gap-2 text-sm font-semibold">
                            <AlertCircle aria-hidden="true" className="size-4 shrink-0" />
                            The pricing model couldn't be imported
                        </span>
                        <button type="button" aria-label="Dismiss" onClick={() => setPricingErrors([])}>
                            <XClose aria-hidden="true" className="text-error-primary size-4" />
                        </button>
                    </div>
                    <p className="text-error-primary text-xs">
                        Nothing was changed. Fix these cells in the workbook and upload it again.
                    </p>
                    <ul className="text-error-primary flex list-disc flex-col gap-1 pl-8 text-xs">
                        {pricingErrors.map((issue, i) => (
                            <li key={`${issue.sheet}-${issue.cell}-${i}`}>
                                {issue.sheet && issue.cell && (
                                    <span className="font-semibold">
                                        {issue.sheet} · {issue.cell}:{" "}
                                    </span>
                                )}
                                {issue.message}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <section className="border-secondary bg-primary shadow-xs overflow-hidden rounded-xl border">
                <header className="border-secondary flex items-center justify-between gap-3 border-b px-4 py-3 sm:px-6 sm:py-4">
                    <div className="hidden md:block">
                        <Tabs
                            selectedKey={tab}
                            onSelectionChange={(k: Key | null) => k !== null && setTab(String(k) as FilesTab)}
                            className="w-max"
                        >
                            <Tabs.List type="button-minimal" size="sm" aria-label="Files views" items={TAB_ITEMS}>
                                {(item) => <Tabs.Item {...item} />}
                            </Tabs.List>
                        </Tabs>
                    </div>
                    {/* Upload ▾ only on the Uploaded Files tab. One menu,
                     * three paths (Import/Export Flow V2): From Pricing
                     * Model / From Broker Documents / Attach Only. Disabled
                     * while any upload or extraction is in flight. */}
                    {tab === "uploaded" && (
                        <UploadMenu
                            onAction={onUploadMenuAction}
                            isDisabled={uploading || uploadBusy || !detail.id}
                            data-autotest="files-uploaded-upload-button"
                        />
                    )}
                </header>

                {/* Generated Files (Andressa's Figma): one row per system
                 * document — type badge, name, "Updated {date}", Download.
                 * Order per the Import/Export Flow V2 design (users' request):
                 * Pricing Model, Quote, Binder, Policy, TRIA. */}
                {tab === "generated" && (
                    <div className="divide-secondary divide-y">
                        {/* OR-1351 — the pricing-model .xlsx, exported live
                         * from the submission. Its stamp is the submission's
                         * own `updatedAt`: the workbook reflects the whole
                         * submission, not one generated resource. */}
                        <DocRow
                            name="Pricing Model"
                            ext="XLSX"
                            data-autotest="files-generated-pricing-model-row"
                            dateLabel={formatDateTime(detail.updatedAt)}
                            loading={generating === "pricing"}
                            disabled={!detail.id || generating !== null}
                            onDownload={onExportPricingModel}
                        />
                        <DocRow
                            name="Quote"
                            ext="DOCX"
                            data-autotest="files-generated-quote-row"
                            dateLabel={formatDateTime(quoteStamp || detail.updatedAt)}
                            loading={generating === "quote"}
                            disabled={!detail.id || generating !== null}
                            onDownload={onGenerateQuote}
                        />
                        <DocRow
                            name="Binder"
                            ext="DOCX"
                            data-autotest="files-generated-binder-row"
                            dateLabel={formatDateTime(policyDocStamp || detail.updatedAt)}
                            loading={generating === "binder"}
                            disabled={generating !== null}
                            onDownload={onGenerateBinder}
                        />
                        <DocRow
                            name="Policy"
                            ext="DOCX"
                            data-autotest="files-generated-policy-row"
                            dateLabel={formatDateTime(policyDocStamp || detail.updatedAt)}
                            loading={generating === "policy"}
                            disabled={!policyId || generating !== null}
                            onDownload={onGeneratePolicy}
                        />
                        {showTria && (
                            <DocRow
                                name="TRIA"
                                ext="DOCX"
                                dateLabel={formatDateTime(policyDocStamp || detail.updatedAt)}
                                loading={generating === "tria"}
                                disabled={!detail.id || generating !== null}
                                onDownload={onGenerateTria}
                            />
                        )}
                    </div>
                )}

                {tab === "uploaded" && state.status === "loading" && (
                    <div className="text-tertiary px-6 py-10 text-sm">Loading documents…</div>
                )}

                {tab === "uploaded" && state.status === "error" && (
                    <div role="alert" className="text-error-primary px-6 py-10 text-sm">
                        Couldn't load documents: {state.message}
                    </div>
                )}

                {tab === "uploaded" && state.status === "ready" && files.length === 0 && (
                    <TableEmptyState
                        icon={Folder}
                        title="No documents uploaded yet"
                        // No CTA here, per Andressa (sep-02): a button would
                        // need its own copy of the three-option menu, so the
                        // header's Upload is the single entry point and the
                        // copy explains the choice instead.
                        description="Upload documents to auto-fill Orion automatically, or attach them for reference only."
                    />
                )}

                {tab === "uploaded" && state.status === "ready" && files.length > 0 && (
                    <Table aria-label="Uploaded files" wrapperClassName="overflow-auto">
                        <Table.Header className="bg-secondary">
                            <Table.Head id="name" isRowHeader className="pl-6">
                                <button
                                    type="button"
                                    onClick={cycleSort}
                                    aria-sort={sortDir === "asc" ? "ascending" : sortDir === "desc" ? "descending" : "none"}
                                    data-autotest="files-uploaded-file-name-sort-button"
                                    className={cx(
                                        "hover:text-primary inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded text-xs font-semibold transition-colors",
                                        sortDir ? "text-primary" : "text-quaternary",
                                    )}
                                >
                                    File Name
                                    {sortDir === "asc" ? (
                                        <ArrowUp aria-hidden="true" className="text-fg-secondary size-3 stroke-[2.25px]" />
                                    ) : sortDir === "desc" ? (
                                        <ArrowDown aria-hidden="true" className="text-fg-secondary size-3 stroke-[2.25px]" />
                                    ) : (
                                        <ChevronSelectorVertical aria-hidden="true" className="text-fg-quaternary size-3 stroke-[2.25px]" />
                                    )}
                                </button>
                            </Table.Head>
                            <Table.Head id="uploadedBy" label="Uploaded by" />
                            <Table.Head id="uploadedAt" label="Uploaded on" />
                            {/* Right-align "Actions" over the download/trash icons — atlas
                             * wraps the head content in a `flex` group, so push it to the
                             * end (the row's action cluster is justify-end + pr-6 too). */}
                            <Table.Head id="actions" label="Actions" className="pr-6 [&>*]:w-full [&>*]:justify-end" />
                        </Table.Header>
                        <Table.Body>
                            {sortedFiles.map((f) => (
                                <Table.Row id={f.id} key={f.id} data-autotest="files-uploaded-table-row">
                                    <Table.Cell className="text-secondary pl-6 text-sm" data-autotest="files-uploaded-file-name-cell">
                                        {f.name}
                                    </Table.Cell>
                                    <Table.Cell className="text-tertiary text-sm" data-autotest="files-uploaded-uploaded-by-cell">
                                        <div className="flex items-center gap-2">
                                            <Avatar size="sm" initials={f.uploadedByInitials} />
                                            <span className="text-primary font-medium">{f.uploadedByName ?? "—"}</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell className="text-tertiary text-sm tabular-nums" data-autotest="files-uploaded-uploaded-at-cell">
                                        {formatDateTime(f.uploadedAt) || "—"}
                                    </Table.Cell>
                                    <Table.Cell className="pr-6">
                                        <div className="flex items-center justify-end gap-1">
                                            <a
                                                href={f.downloadUrl || undefined}
                                                target="_blank"
                                                rel="noreferrer"
                                                aria-label={`Download ${f.name}`}
                                                data-autotest="files-uploaded-download-link"
                                                className={cx(
                                                    "size-8 inline-flex items-center justify-center rounded-md",
                                                    f.downloadUrl
                                                        ? "text-fg-quaternary hover:text-fg-secondary hover:bg-secondary cursor-pointer"
                                                        : "text-fg-disabled pointer-events-none",
                                                )}
                                            >
                                                <Download01 className="size-4" />
                                            </a>
                                            <ButtonUtility
                                                color="tertiary"
                                                size="sm"
                                                icon={Trash02}
                                                tooltip="Delete"
                                                onClick={() => void onDelete(f.id)}
                                                data-autotest="files-uploaded-delete-button"
                                            />
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                )}
            </section>

            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => beginUpload("attach-only", e.target.files)} />
            {/* Broker-documents picker — extraction formats only (the panel's
              * "Supported formats" line). Regular system file window, per
              * Andressa's confirmation. */}
            <input
                ref={brokerInputRef}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx"
                className="hidden"
                onChange={(e) => beginUpload("broker", e.target.files)}
            />
            {/* Pricing model: one workbook at a time, .xlsx only. */}
            <input
                ref={pricingInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => void onPricingFileSelected(e.target.files)}
            />

            {/* Figma popup 2 — Duplicate File Detected. Keep both uploads
              * as-is (the backend stores both records); Replace existing
              * deletes the same-named record(s) first. */}
            <WarningDialog
                isOpen={duplicatePrompt !== null}
                onClose={() => setDuplicatePrompt(null)}
                title="Duplicate File Detected"
                data-autotest="files-uploaded-duplicate-file-dialog"
                description="Do you want to replace the existing file with a new version or keep both files?"
                actions={
                    <>
                        <Button color="secondary" size="md" onClick={() => setDuplicatePrompt(null)}>
                            Cancel
                        </Button>
                        <Button
                            size="md"
                            onClick={() => {
                                const prompt = duplicatePrompt;
                                setDuplicatePrompt(null);
                                if (prompt) void runUpload(prompt.kind, prompt.files, duplicateChoice === "replace");
                            }}
                        >
                            Upload
                        </Button>
                    </>
                }
            >
                <AriaRadioGroup
                    aria-label="Duplicate file resolution"
                    value={duplicateChoice}
                    onChange={(v) => setDuplicateChoice(v as "keep" | "replace")}
                    className="flex flex-col gap-2"
                >
                    {(["keep", "replace"] as const).map((value) => (
                        <AriaRadio
                            key={value}
                            value={value}
                            className="group flex cursor-pointer items-center gap-2 text-sm outline-none"
                        >
                            {/* The native input is visually hidden, so the ring
                              * has to live on the drawn circle or a keyboard
                              * user sees no focus at all. Driven by the
                              * component's own data attributes. */}
                            <span
                                aria-hidden="true"
                                className={cx(
                                    "size-4 shrink-0 rounded-full border transition",
                                    "group-data-[selected]:border-brand-solid group-data-[selected]:border-[5px]",
                                    "group-data-[focus-visible]:ring-focus-ring group-data-[focus-visible]:ring-2",
                                    "group-data-[focus-visible]:ring-offset-2",
                                    duplicateChoice === value ? "border-brand-solid border-[5px]" : "border-primary bg-primary",
                                )}
                            />
                            {/* Same hue as the subtitle, a step heavier — per
                              * the Figma. */}
                            <span className="text-tertiary text-sm font-medium">{value === "keep" ? "Keep both" : "Replace existing"}</span>
                        </AriaRadio>
                    ))}
                </AriaRadioGroup>
            </WarningDialog>

            {/* From Pricing Model overwrites whole sections, not just the
              * fields a document happens to carry, so it gets its own,
              * blunter warning before the picker opens. */}
            <WarningDialog
                isOpen={confirmPricingImport}
                onClose={() => setConfirmPricingImport(false)}
                title="This action will overwrite your submission data"
                description="Importing a pricing model replaces the Schedule, Loss Runs and Premium data already entered in Orion."
                maxWidth={456}
                data-autotest="files-pricing-import-confirm-dialog"
                actions={
                    <>
                        <Button color="secondary" size="md" onClick={() => setConfirmPricingImport(false)}>
                            Cancel
                        </Button>
                        <Button
                            size="md"
                            onClick={() => {
                                setConfirmPricingImport(false);
                                pricingInputRef.current?.click();
                            }}
                        >
                            Continue
                        </Button>
                    </>
                }
            />

            {/* Figma confirmation popup 4 — shown before the broker-documents
              * picker opens: extraction overwrites previously extracted
              * fields. */}
            <WarningDialog
                isOpen={confirmBrokerUpload}
                onClose={() => setConfirmBrokerUpload(false)}
                title="This action will update your submission data"
                description="Uploading a new ACORD or supplemental file will replace any previously extracted data for those fields."
                // One step wider so the title holds a single line, per the mock.
                maxWidth={456}
                data-autotest="files-broker-import-confirm-dialog"
                actions={
                    <>
                        <Button color="secondary" size="md" onClick={() => setConfirmBrokerUpload(false)}>
                            Cancel
                        </Button>
                        <Button
                            size="md"
                            onClick={() => {
                                setConfirmBrokerUpload(false);
                                brokerInputRef.current?.click();
                            }}
                        >
                            Continue
                        </Button>
                    </>
                }
            />
        </div>
    );
}
